import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";
import { ensureAvatarCached, getUserProfile } from "./userProfile";

export default function Menu({ onRefresh }) {
  const [user, setUser] = useState(null);
  const [cachedPhotoUrl, setCachedPhotoUrl] = useState("");
  const [photoError, setPhotoError] = useState(false);
  const [avatarBlocked, setAvatarBlocked] = useState(false);
  const [isMenuHidden, setIsMenuHidden] = useState(false);

  const AVATAR_BACKOFF_KEY = "avatarImageBackoff";
  const AVATAR_BACKOFF_MS = 24 * 60 * 60 * 1000;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return unsubscribe;
  }, []);

  const providerPhotoUrl =
    user?.providerData?.find((provider) => provider.photoURL)?.photoURL || "";
  const sourcePhotoUrl = user?.photoURL || providerPhotoUrl || "";
  const photoUrl =
    cachedPhotoUrl || (!avatarBlocked ? sourcePhotoUrl : "");

  useEffect(() => {
    const key = `${AVATAR_BACKOFF_KEY}:${user?.uid || "anon"}`;
    const lastFail = Number(localStorage.getItem(key) || "0");
    setAvatarBlocked(
      !!lastFail && Date.now() - lastFail < AVATAR_BACKOFF_MS
    );
  }, [user?.uid, sourcePhotoUrl, AVATAR_BACKOFF_MS, AVATAR_BACKOFF_KEY]);

  useEffect(() => {
    let isActive = true;
    const loadProfilePhoto = async () => {
      if (!user?.uid) {
        setCachedPhotoUrl("");
        return;
      }

      try {
        const profile = await getUserProfile(user.uid);
        if (!isActive) return;
        if (profile?.photoUrl) {
          setCachedPhotoUrl(profile.photoUrl);
        }
        if (sourcePhotoUrl) {
          const cached = await ensureAvatarCached({
            uid: user.uid,
            photoURL: sourcePhotoUrl,
          });
          if (isActive && cached) {
            setCachedPhotoUrl(cached);
          }
        }
      } catch (error) {
        console.warn("Failed to cache avatar:", error);
      }
    };
    loadProfilePhoto();
    return () => {
      isActive = false;
    };
  }, [user?.uid, sourcePhotoUrl]);

  useEffect(() => {
    setPhotoError(false);
  }, [photoUrl]);

  useEffect(() => {
    let lastScrollY = window.scrollY;
    const directionThreshold = 4;
    const bottomThreshold = 16;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollingDown = currentScrollY > lastScrollY + directionThreshold;
      const scrollingUp = currentScrollY < lastScrollY - directionThreshold;
      const viewportBottom = currentScrollY + window.innerHeight;
      const pageBottom = document.documentElement.scrollHeight;
      const atPageBottom = viewportBottom >= pageBottom - bottomThreshold;

      if (scrollingDown && atPageBottom) {
        setIsMenuHidden(true);
      } else if (scrollingUp || currentScrollY <= 0) {
        setIsMenuHidden(false);
      }

      lastScrollY = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div className={`menu${isMenuHidden ? " menu--hidden" : ""}`}>
      <div className="menu__brand" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <rect x="4" y="4" width="16" height="16" rx="5" />
          <circle cx="12" cy="12" r="3.8" />
          <circle cx="17.3" cy="6.8" r="1" fill="currentColor" stroke="none" />
        </svg>
      </div>

      <div className="menu__primary">
        <NavLink
          to="/"
          className={({ isActive }) =>
            isActive ? "menu-item active" : "menu-item"
          }
          onClick={(e) => {
            if (e.target.closest(".menu-item.active")) {
              e.preventDefault();
              window.scrollTo(0, 0);
              if (onRefresh) {
                onRefresh();
              }
            }
          }}
          title="Home"
        >
          <svg width="24" height="24" viewBox="0 0 24 24">
            <path d="M22 23h-6.001a1 1 0 0 1-1-1v-5.455a2.997 2.997 0 1 0-5.993 0V22a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V11.543a1.002 1.002 0 0 1 .31-.724l10-9.543a1.001 1.001 0 0 1 1.38 0l10 9.543a1.002 1.002 0 0 1 .31.724V22a1 1 0 0 1-1 1Z" />
          </svg>
          <span className="menu-item__label">Home</span>
        </NavLink>

        <NavLink
          to="/accounts"
          className={({ isActive }) =>
            isActive ? "menu-item active" : "menu-item"
          }
          title="Explore"
        >
          <svg width="24px" height="24px" viewBox="0 0 16 16">
            <path d="M3 1H1V3H3V1Z"></path>
            <path d="M3 5H1V7H3V5Z"></path>
            <path d="M1 9H3V11H1V9Z"></path>
            <path d="M3 13H1V15H3V13Z"></path>
            <path d="M15 1H5V3H15V1Z"></path>
            <path d="M15 5H5V7H15V5Z"></path>
            <path d="M5 9H15V11H5V9Z"></path>
            <path d="M15 13H5V15H15V13Z"></path>
          </svg>
          <span className="menu-item__label">Explore</span>
        </NavLink>

        {user && (
          <NavLink
            to="/add-post"
            className={({ isActive }) =>
              isActive ? "menu-item active" : "menu-item"
            }
            title="Create"
          >
            <svg width="24px" height="24px" viewBox="0 0 24 24">
              <path d="M12 5v14M5 12h14" strokeWidth="2" />
            </svg>
            <span className="menu-item__label">Create</span>
          </NavLink>
        )}
      </div>

      <div className="menu__secondary">
        <NavLink
          to="/account"
          className={({ isActive }) =>
            isActive ? "menu-item active" : "menu-item"
          }
          title="Profile"
        >
          <div className="menu-item__avatar">
            {photoUrl && !photoError ? (
              <img
                src={photoUrl}
                alt=""
                className="menu-item__avatar-image"
                referrerPolicy="no-referrer"
                onError={() => {
                  setPhotoError(true);
                  const key = `${AVATAR_BACKOFF_KEY}:${user?.uid || "anon"}`;
                  localStorage.setItem(key, Date.now().toString());
                  setAvatarBlocked(true);
                }}
              />
            ) : (
              <span className="silhouette-icon" aria-hidden="true"></span>
            )}
          </div>
          <span className="menu-item__label">Profile</span>
        </NavLink>

        {user && (
          <NavLink
            to="/admin-grid"
            className={({ isActive }) =>
              isActive ? "menu-item admin-grid-link active" : "menu-item admin-grid-link"
            }
            title="Admin"
          >
            <svg width="24px" height="24px" viewBox="0 0 24 24">
              <path
                d="M3 3h8v8H3zM13 3h8v8h-8zM3 13h8v8H3zM13 13h8v8h-8z"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
              />
            </svg>
            <span className="menu-item__label">Admin</span>
          </NavLink>
        )}
      </div>
    </div>
  );
}
